import React from "react";

import * as strings from "../../strings";

import {
	isValidEmail,
	stringPrettify,
	toSnakeCase,
	validatePassword,
	validatePhoneNumber,
} from "./StringUtils";

export const validateValue: any = (
	value: any,
	meta: any,
	validators: any,
	fields: any,
	defaultMeta: any,
) => {
	if (Array.isArray(value)) {
		for (const item of value) {
			const result = validateValue(item, meta, validators, fields, defaultMeta);
			if (!result.valid) {
				return result;
			}
		}
		return { valid: true, message: null };
	}

	const resolvedMeta = meta === undefined ? defaultMeta : meta;

	const trimmedValue = typeof value === "string" ? value.trim() : value;

	const required = validators.includes("required");

	if (!trimmedValue || trimmedValue.length === 0) {
		if (required) {
			return {
				valid: false,
				message: strings.FORM_ERROR_REQUIRED,
			};
		}
		return {
			valid: true,
			message: null,
		};
	}

	for (let validator of validators) {
		let data: any = null;
		if (Array.isArray(validator)) {
			data = validator[1];
			validator = validator[0];
		}

		switch (validator) {
			case "email":
				if (!isValidEmail(trimmedValue)) {
					return {
						valid: false,
						message: strings.FORM_ERROR_EMAIL,
					};
				}
				break;
			case "password":
				const passwordValidation = validatePassword(trimmedValue);
				if (!passwordValidation.success) {
					return {
						valid: false,
						message: passwordValidation.reason,
					};
				}
				break;

			case "phoneNumber":
				if (typeof value !== "string") {
					break;
				}

				const phoneNumberValidation = validatePhoneNumber(
					trimmedValue,
					resolvedMeta?.country,
				);
				if (!phoneNumberValidation.success) {
					return {
						valid: false,
						message: phoneNumberValidation.reason,
					};
				}
				break;

			case "ascii":
				// eslint-disable-next-line no-control-regex
				if (!/^[\x00-\x7F]*$/.test(value)) {
					return {
						valid: false,
						message: "must be ASCII only (no special chars)",
					};
				}
				break;

			case "match":
				if (typeof value === "string" && fields[data].value !== value) {
					return {
						valid: false,
						message: strings.FORM_ERROR_MATCH.map(
							(text) =>
								`${text}: ${fields[data].meta?.name || stringPrettify(data)}`,
						),
					};
				}
				break;

			case "min":
				let number = parseFloat(trimmedValue);
				if (number < data) {
					return {
						valid: false,
						message: strings.FORM_ERROR_MIN.with({ value: data }),
					};
				}
				break;

			case "max":
				number = parseFloat(trimmedValue);
				if (number > data) {
					return {
						valid: false,
						message: strings.FORM_ERROR_MAX.with({ value: data }),
					};
				}
				break;

			case "lowerCase":
				if (typeof value === "string" && value !== value.toLowerCase()) {
					return {
						valid: false,
						message: strings.FORM_ERROR_LOWERCASE,
					};
				}
				break;

			case "snakeCase":
				if (typeof value === "string" && value !== toSnakeCase(value)) {
					return {
						valid: false,
						message: "Must be snake_case",
					};
				}
				break;

			case "notContain":
				if (
					typeof value === "string" &&
					Array.isArray(data) &&
					data.some((str) => value.includes(str))
				) {
					return {
						valid: false,
						message: strings.FORM_ERROR_NOT_CONTAIN.map(
							(text) =>
								`${text}: ${data
									.map(
										(str: string) =>
											`'${str}'${str.trim().length ? "" : " (whitespace)"}`,
									)
									.join(", ")}`,
						),
					};
				}
				break;

			case "length":
				if (typeof trimmedValue === "string" && trimmedValue.length < data) {
					return {
						valid: false,
						message: strings.FORM_ERROR_LENGTH.with({
							length: data,
						}),
					};
				}
				break;

			case "commaSeparatedUniqueMinLength":
				if (typeof value !== "string") {
					break;
				}

				const trimmedSplitValue = value
					.split(",")
					.map((item) => item.trim())
					.filter(Boolean);
				const uniqueValues = new Set(trimmedSplitValue);
				const minValue = Number(data) || 1;

				if (uniqueValues.size < minValue) {
					return {
						valid: false,
						message: strings.FORM_ERROR_UNIQUE_VALUES.with({
							number: minValue,
						}),
					};
				}

				break;

			case "delimeterSeparatedEmails":
				if (typeof value !== "string") {
					break;
				}

				const splitValue = value.split(",");
				const invalidEntries = splitValue.filter(
					(value) => value.trim() && !isValidEmail(value.trim()),
				);
				if (invalidEntries.length > 0) {
					return {
						valid: false,
						message: strings.FORM_ERROR_COMMA_SEPARATED_EMAILS.map(
							(text) =>
								`${text}: ${invalidEntries
									.map((entry) => entry.trim())
									.join(", ")}`,
						),
					};
				}
				break;

			case "greaterThan":
				if (!fields) {
					break;
				}

				const comparisonValue = fields[data].value || fields[data].defaultValue;

				if (typeof value !== "number" || value <= comparisonValue) {
					return {
						valid: false,
						message: `Value must be greater than ${
							fields[data].meta?.name || stringPrettify(data)
						}`,
					};
				}

				break;
			default:
				break;
		}
	}
	return {
		valid: true,
		message: null,
	};
};

export const extractCommaDemilitedEmails = (value: string) =>
	value
		.split(",")
		.map((item) => ({
			email: item.trim(),
		}))
		.filter((item) => Boolean(item.email));

export const FormContext = React.createContext(null);
